import React from "react";
import { graphql } from "react-relay";

import { faArrowLeft, faEdit, faHistory, faXmark } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import cx from "classnames";
import { commitLocalUpdate } from "relay-runtime";

import { encodeUserId } from "~/lib/prose/plugins/context";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { inVideocallMode } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import { useMeetingModalContext } from "~/meetings/MeetingModal/MeetingModalContext";
import { environment } from "~/models";
import Button from "~/ui/button";
import IconButton from "~/ui/button/IconButton";

import { useAskCopilotSendMessageViaConversation } from "./AskFellowHooks";
import AskFellowLandingPage from "./AskFellowLandingPage";
import { type AskCopilotConversation, AskFellowPanelInner } from "./AskFellowPanelInner";
import RecentChats from "./RecentChats";
import AskFellowPanelSkeleton from "./SkeletonLoader";

import type { AskFellowHooks_useAskCopilotSendMessage_conversation$key } from "~/__generated__/AskFellowHooks_useAskCopilotSendMessage_conversation.graphql";
import type { AskFellowPanelConversationsQuery as AskFellowPanelConversationsQueryType } from "~/__generated__/AskFellowPanelConversationsQuery.graphql";
import type {
	AskFellowPanelQuery$data,
	AskFellowPanelQuery as AskFellowPanelQueryType,
} from "~/__generated__/AskFellowPanelQuery.graphql";

import navPanelStyles from "~/nav/panels/shared/NavPanel.module.css";
import panelStyles from "~/ui/CollapsiblePanel/CollapsiblePanel.module.css";

import styles from "./AskFellowPanel.module.css";

const AskFellowPanelQuery = graphql`
	query AskFellowPanelQuery {
		askCopilotConversations {
			id
			updatedAt
			title
		}
	}
`;

const AskFellowPanelConversationsQuery = graphql`
	query AskFellowPanelConversationsQuery($conversationId: ID!) {
		viewer {
			account {
				allowAiFeatures
			}
			conversation: node(id: $conversationId) {
				__typename
				... on AskCopilotConversation {
					note {
						id
					}

					id
					...AskFellowPanelInnerFragment @relay(mask: false)
					...AskFellowPanelInnerConversationListener_conversation
					...AskFellowHooks_useAskCopilotSendMessage_conversation
					...AskFellowSuggestedQuestions_EmptyState_conversation
					...AskFellowPanelInner_ChatMessage_conversation
					...AskFellowContext_conversation

					context {
						name
						filters
					}
				}
			}
		}
	}
`;

type ConversationsMessageType =
	| { type: "TITLE_CHANGE"; conversation_id: string; title: string }
	| { type: "UPDATED_AT_CHANGE"; conversation_id: string; updated_at: string };

function AskFellowPanelContent({
	conversationId,
	compactLandingPage,
	recentConversations,
	noteId,
	inModal,
	onClose,
	refetch,
}: {
	conversationId: string;
	compactLandingPage?: boolean;
	recentConversations: AskFellowPanelQuery$data["askCopilotConversations"];
	noteId?: string;
	inModal?: boolean;
	onClose?: () => void;
	refetch: () => void;
}) {
	const [inConversation, setInConversation] = React.useState(false);
	const [inConversationFromHistory, setInConversationFromHistory] = React.useState(false);
	const [inHistory, setInHistory] = React.useState(false);
	const [activeConversationId, setActiveConversationId] = React.useState<string>(conversationId);
	const [previousConversationId, setPreviousConversationId] = React.useState<string>(conversationId);

	React.useEffect(() => {
		setActiveConversationId(conversationId);
		setPreviousConversationId(conversationId);
	}, [conversationId]);

	const data = useLazyLoadQuery<AskFellowPanelConversationsQueryType>(
		AskFellowPanelConversationsQuery,
		{ conversationId: activeConversationId },
		{ fetchPolicy: "store-and-network" },
	);
	const conversation = data?.viewer?.conversation as AskCopilotConversation;

	// WebSocket connection for real-time title updates
	React.useEffect(() => {
		const groupName = `ask_copilot_conversations.${encodeUserId(
			parseInt(`${window.INITIAL_STATE.user?.id ?? "0"}`, 10),
		).replace(/=/g, "")}`;
		const group = WebsocketClient.getInstance().getOrJoinGroup(groupName, {});

		function listener(data: ConversationsMessageType) {
			if (data.type === "TITLE_CHANGE") {
				const conversationId = data.conversation_id;
				const title = data.title;

				function delay(ms: number) {
					return new Promise(resolve => setTimeout(resolve, ms));
				}

				(async () => {
					for (let i = 3; i <= title.length; i++) {
						commitLocalUpdate(environment, store => {
							const conversation = store.get(conversationId);
							if (conversation) {
								conversation.setValue(title.slice(0, i), "title");
							}
						});
						await delay(40);
					}
				})();
			}
			if (data.type === "UPDATED_AT_CHANGE") {
				const conversationId = data.conversation_id;
				const updatedAt = data.updated_at;

				commitLocalUpdate(environment, store => {
					const conversation = store.get(conversationId);
					if (conversation) {
						conversation.setValue(updatedAt, "updatedAt");
					}
				});
			}
		}

		group.on("message", listener);

		return () => {
			group.off("message", listener);
			group.close();
		};
	}, []);

	const onConversationSelected = React.useCallback(
		(conversationId: string) => {
			if (inHistory) {
				setInConversationFromHistory(true);
			}
			setActiveConversationId(conversationId);
			setInConversation(true);
			setInHistory(false);
		},
		[inHistory],
	);

	const backButtonClicked = React.useCallback(() => {
		setActiveConversationId(previousConversationId);
		if (inConversationFromHistory) {
			setInHistory(true);
		} else {
			setInHistory(false);
		}
		setInConversationFromHistory(false);
		setInConversation(false);
		refetch();
	}, [previousConversationId, refetch, inConversationFromHistory]);

	const createNewChatClicked = React.useCallback(() => {
		setActiveConversationId(previousConversationId);
		setInConversation(false);
		setInHistory(false);
		setInConversationFromHistory(false);
		refetch();
	}, [previousConversationId, refetch]);
	const aiFeaturesDisabled = data?.viewer?.account?.allowAiFeatures !== true;

	const [_sendMessage] = useAskCopilotSendMessageViaConversation(
		conversation as AskFellowHooks_useAskCopilotSendMessage_conversation$key,
		true,
	);
	const sendMessage = React.useCallback(
		(content: string, messageId?: string, contentForDisplay?: string) => {
			setInConversation(true);
			return _sendMessage(content, messageId, contentForDisplay);
		},
		[_sendMessage],
	);

	const contextFooterRef = React.useRef<HTMLDivElement>(null);

	const headerLabel = inVideocallMode ? "Get caught up on your meeting" : "Ask about your meetings";

	const headerTitle = React.useMemo(() => {
		if (inHistory) {
			return "Previous Chats";
		}
		if (inConversation) {
			return conversation?.title || "New chat";
		}
		return "Ask Fellow";
	}, [inConversation, conversation, inHistory]);

	const openHistoryPage = React.useCallback(() => {
		setInHistory(true);
	}, []);

	const inHistoryOrConversation = inConversation || inHistory;

	const inLandingPage = !inConversation && !inHistory;

	const hasRecentConversations = recentConversations.length > 0;

	if (!conversation || !activeConversationId) {
		return (
			<section
				style={{ padding: "10px" }}
				className={cx(navPanelStyles.panel, panelStyles.panelBase, styles.mainPanel, styles.mainContentArea)}
			>
				<p>Something went wrong!</p>
				<Button onClick={refetch}>Please click here to reload Ask Fellow</Button>
			</section>
		);
	}

	return (
		<section className={cx(navPanelStyles.panel, panelStyles.panelBase, styles.mainPanel)}>
			<header
				className={cx(styles.panelHeader, {
					[styles.compactPanelHeader]: compactLandingPage,
					[styles.inModal]: inModal,
					[styles.inExtension]: inVideocallMode,
					[styles.extraPadding]: !inHistoryOrConversation && !inVideocallMode,
				})}
			>
				{inHistoryOrConversation && (
					<div className={styles.headerActions}>
						<IconButton icon={faArrowLeft} onClick={backButtonClicked} title="New Chat" variant="tertiary" />
					</div>
				)}
				<h1>{headerTitle}</h1>
				<div className={styles.headerRightActions}>
					{inHistoryOrConversation && (
						<IconButton icon={faEdit} onClick={createNewChatClicked} title="New Chat" variant="tertiary" />
					)}
					{inLandingPage && hasRecentConversations && (
						<IconButton icon={faHistory} onClick={openHistoryPage} title="Previous Chats" variant="tertiary" />
					)}
					{onClose && <IconButton icon={faXmark} onClick={onClose} title="Close" variant="tertiary" />}
				</div>
			</header>
			<div
				className={cx(styles.mainContentArea, {
					[styles.mainContentAreaOverflow]: !inConversation,
					[styles.inModal]: inModal,
				})}
			>
				{inHistory ? (
					<RecentChats
						conversations={recentConversations}
						onConversationSelected={onConversationSelected}
						className={styles.recentChatsSection}
					/>
				) : !inConversation ? (
					<div
						className={cx(styles.contentSection, {
							[styles.inModal]: inModal,
							[styles.inExtension]: inVideocallMode,
							[styles.topMargin]: inModal || inVideocallMode,
						})}
					>
						<AskFellowLandingPage
							headerLabel={headerLabel}
							conversation={conversation}
							compactLandingPage={compactLandingPage}
							onMessageFormSubmit={sendMessage}
							aiFeaturesDisabled={aiFeaturesDisabled}
							contextFooterRef={contextFooterRef}
							sendMessage={sendMessage}
							noteId={noteId}
							refetch={refetch}
						/>
					</div>
				) : (
					<div
						className={cx(styles.contentSection, { [styles.inModal]: inModal, [styles.inExtension]: inVideocallMode })}
					>
						<AskFellowPanelInner
							aiFeaturesDisabled={aiFeaturesDisabled}
							conversation={conversation}
							onSelectedConversation={onConversationSelected}
							inModal={inModal}
						/>
					</div>
				)}
			</div>
		</section>
	);
}

function AskFellowPanelWrapper({
	noteId,
	compactLandingPage,
	onClose,
}: {
	noteId?: string;
	compactLandingPage?: boolean;
	onClose?: () => void;
}) {
	const [fetchKey, setFetchKey] = React.useState<number>(0);

	const refetch = React.useCallback(() => {
		setFetchKey(prev => prev + 1);
	}, []);

	const data = useLazyLoadQuery<AskFellowPanelQueryType>(
		AskFellowPanelQuery,
		{},
		{ fetchPolicy: "store-and-network", fetchKey },
	);

	const meetingModalContext = useMeetingModalContext();
	const inModal = meetingModalContext !== null;

	// Get the first conversation, which should be the newest one
	const latestConversation = data?.askCopilotConversations?.[0];
	const conversationId = latestConversation?.id;

	const recentConversations = React.useMemo(() => {
		if (!data?.askCopilotConversations) return [];

		// Skip the first conversation (index 0) as it's the active one
		return data?.askCopilotConversations.slice(1);
	}, [data?.askCopilotConversations]);

	return (
		<AskFellowPanelContent
			conversationId={conversationId}
			compactLandingPage={compactLandingPage}
			recentConversations={recentConversations}
			noteId={noteId}
			inModal={!!inModal}
			refetch={refetch}
			onClose={onClose}
		/>
	);
}

export default withSuspense(<AskFellowPanelSkeleton />, AskFellowPanelWrapper);
